import React from 'react';
import { Skeleton } from 'src/components/ui/skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';

interface MetricChartProps {
  className?: string;
  title?: string;
  data: TimeSeriesDataPoint[];
  metrics: Array<{
    key: string;
    name: string;
    color: string;
  }>;
  isLoading: boolean;
  isNormalized?: boolean;
}

const MetricChart: React.FC<MetricChartProps> = ({ className, title, data, metrics, isLoading, isNormalized = false }) => {
  // Normalize data for chart display while preserving original values
  const processedData = React.useMemo(() => {
    if (!isNormalized || data.length === 0 || metrics.length === 0) {
      return data.map(point => ({
        ...point,
        originalValues: { ...point }, // Store original values for tooltips
      }));
    }

    const enabledMetricKeys = metrics.map(metric => metric.key);

    // Find min and max values for each enabled metric across all data points
    const metricRanges: Record<string, { min: number; max: number }> = {};

    enabledMetricKeys.forEach(key => {
      const values = data
        .map(point => point[key] as number)
        .filter(val => typeof val === 'number' && !isNaN(val));

      if (values.length > 0) {
        metricRanges[key] = {
          min: Math.min(...values),
          max: Math.max(...values),
        };
      }
    });

    // Normalize the data points (0-100 scale)
    return data.map(point => {
      const normalizedPoint = { ...point, originalValues: { ...point } } as any;

      enabledMetricKeys.forEach(key => {
        const value = point[key] as number;
        const range = metricRanges[key];

        if (range && typeof value === 'number' && !isNaN(value)) {
          const rangeSpan = range.max - range.min;
          if (rangeSpan > 0) {
            // Normalize to 0-100 scale
            normalizedPoint[key] = ((value - range.min) / rangeSpan) * 100;
          } else {
            normalizedPoint[key] = 50; // If all values are the same, put them in the middle
          }
        }
      });

      return normalizedPoint;
    });
  }, [data, isNormalized, metrics]);

  const formatDateShort = (date: string) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  const formatDateFull = (date: string) => {
    return new Date(date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatMetricValue = (value: number, metricKey: string): string => {
    if (!isFinite(value) || isNaN(value)) {
      return 'N/A';
    }
    if (metricKey === 'ctr' || metricKey === 'video_completion_rate' || metricKey === 'audio_completion_rate') {
      return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)}%`;
    }
    if (metricKey === 'spend' || metricKey === 'cpa' || metricKey === 'cpm') {
      if (value >= 1000) {
        return `$${(value / 1000).toFixed(1)}k`;
      }
      return `$${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)}`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k`;
    }
    return value.toString();
  };

  if (isLoading) {
    return (
      <div className={`rounded-2xl bg-white border border-gray-400 p-4 lg:p-8 flex-1 min-w-[200px] ${className}`}>
        {title && (
          <div className="text-2xl font-light mb-4">
            {title}
          </div>
        )}
        <Skeleton className="w-full h-[300px]" />
      </div>
    );
  }

  return (
    <div className={`rounded-2xl bg-white border border-gray-400 p-4 lg:p-8 flex-1 min-w-[200px] ${className}`}>
      {title && (
        <div className="text-2xl font-light mb-4">
          {title}
        </div>
      )}
      <div className="h-[200px]">
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <LineChart
            data={processedData}
            margin={{
              top: 10,
              right: metrics.length > 1 ? 0 : 20,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateShort}
              minTickGap={30}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={(value) =>
                isNormalized ? `${value.toFixed(0)}%` : formatMetricValue(value, metrics[0].key)
              }
            />
            {metrics.length > 1 && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tickFormatter={(value) =>
                  isNormalized ? `${value.toFixed(0)}%` : formatMetricValue(value, metrics[1].key)
                }
              />
            )}
            <Tooltip
              labelFormatter={formatDateFull}
              formatter={(value: number, name: string, props: any) => {
                const metric = metrics.find(m => m.name === name);
                if (!metric) return [value, name];

                // If normalized, show original values in tooltip
                if (isNormalized && props.payload && props.payload.originalValues) {
                  const originalValue = props.payload.originalValues[metric.key];
                  if (typeof originalValue === 'number') {
                    return [formatMetricValue(originalValue, metric.key), metric.name];
                  }
                }

                return [formatMetricValue(value, metric.key), metric.name];
              }}
            />
            <Legend />
            {metrics.map((metric, index) => (
              <Line
                key={metric.key}
                type="monotone"
                dataKey={metric.key}
                name={metric.name}
                stroke={metric.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                yAxisId={index === 0 ? "left" : "right"}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MetricChart;